import React, { useEffect, useState } from "react";
import { Zap, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

export function AutoApproveToggle() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/onboarding-config")
      .then((r) => setCfg(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load onboarding settings"));
  }, []);

  const onChange = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/onboarding-config", { auto_approve_applications: next });
      setCfg(r.data);
      toast.success(next ? "Auto approval of onboarding requests is ON" : "Auto approval of onboarding requests is OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update setting");
    } finally {
      setSaving(false);
    }
  };

  const enabled = !!cfg?.auto_approve_applications;
  const canEdit = !!cfg?.can_edit;

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-start gap-4"
      data-testid="auto-approve-card"
    >
      <div className="mt-0.5 h-9 w-9 rounded-xl bg-[#0A2540] text-[#D4AF37] flex items-center justify-center shrink-0">
        <Zap size={16} />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2">
          <p className="font-bold text-[#0A2540]" data-testid="auto-approve-label">Auto Approve Onboarding Request</p>
          <span
            data-testid="auto-approve-status"
            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
            }`}
          >
            {cfg ? (enabled ? "On" : "Off") : "…"}
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500" data-testid="auto-approve-helper">
          When on, new lawyer applications are approved automatically at the first onboarding stage only: a lawyer
          profile is created and the applicant can log in to complete eKYC, KYC and document onboarding. It does
          not verify the lawyer, list them publicly, or make them bookable — final verification stays manual.
          Applications already pending are not affected.
        </p>
        {cfg?.updated_at && (
          <p className="mt-1 text-[11px] text-slate-400" data-testid="auto-approve-updated">
            Last changed {cfg.updated_at.slice(0, 16).replace("T", " ")} UTC
            {cfg.updated_by?.email ? ` by ${cfg.updated_by.email}` : ""}
          </p>
        )}
        {cfg && !canEdit && (
          <p className="mt-1 text-[11px] text-slate-400 flex items-center gap-1" data-testid="auto-approve-readonly">
            <Lock size={10} /> Only a Super Admin can change this setting.
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 pt-1">
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
        <Switch
          data-testid="auto-approve-toggle"
          checked={enabled}
          disabled={!cfg || !canEdit || saving}
          onCheckedChange={onChange}
          aria-label="Auto Approve Onboarding Request"
          className="data-[state=checked]:bg-emerald-600"
        />
      </div>
    </div>
  );
}
